// VELLA 2.0 -- Task Progress Panel
// Real-time WebSocket task tracking with floating panel UI
// Built by AMPLIFX Advertising Agency

import React, { useState, useEffect, useRef, useCallback } from "react";

const BLUE = "#3B82F6";
const GREEN = "#10B981";
const RED = "#EF4444";
const YELLOW = "#F59E0B";
const GRAY = "#6B7280";

const STATUS_CONFIG = {
  queued: { color: GRAY, icon: "o", label: "Queued" },
  started: { color: BLUE, icon: "~", label: "Running" },
  processing: { color: BLUE, icon: ">", label: "Processing" },
  progress: { color: YELLOW, icon: "-", label: "In Progress" },
  completed: { color: GREEN, icon: "OK", label: "Completed" },
  failed: { color: RED, icon: "X", label: "Failed" },
};

function TaskItem({ task }) {
  const config = STATUS_CONFIG[task.status] || STATUS_CONFIG.queued;
  const progressPct = task.progress || 0;

  return (
    <div style={{
      padding: "12px 16px",
      borderBottom: "1px solid #F3F4F6",
      transition: "background 0.2s",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14 }}>{config.icon}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1F2937", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {task.type?.replace(/_/g, " ") || "Task"}
          </span>
        </div>
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          color: config.color,
          background: config.color + "15",
          padding: "2px 8px",
          borderRadius: 12,
        }}>
          {config.label}
        </span>
      </div>
      {task.message && (
        <p style={{ fontSize: 12, color: GRAY, margin: "4px 0 6px 22px", lineHeight: 1.4 }}>
          {task.message}
        </p>
      )}
      {(task.status === "started" || task.status === "processing" || task.status === "progress") && (
        <div style={{ marginLeft: 22, height: 4, background: "#E5E7EB", borderRadius: 2, overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: `${Math.max(progressPct, 10)}%`,
            background: `linear-gradient(90deg, ${BLUE}, #60A5FA)`,
            borderRadius: 2,
            transition: "width 0.5s ease",
          }} />
        </div>
      )}
      <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4, marginLeft: 22 }}>
        {task.createdAt ? new Date(task.createdAt).toLocaleTimeString() : ""}
      </div>
    </div>
  );
}

export default function TaskProgress({ apiBase }) {
  const [tasks, setTasks] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const reconnectAttempts = useRef(0);

  const backendUrl = apiBase || (process.env.REACT_APP_API_URL || "https://vella-backend-production.up.railway.app");
  const wsUrl = backendUrl.replace(/^http/, "ws") + "/ws";

  // Exponential backoff with jitter so a 1-hour outage produces ~12
  // attempts (capped at 60s), not 720. Resets on successful connection.
  const scheduleReconnect = useCallback((connectFn) => {
    reconnectAttempts.current = Math.min(reconnectAttempts.current + 1, 8);
    const base = Math.min(60000, 1000 * Math.pow(2, reconnectAttempts.current));
    const delay = base + Math.floor(Math.random() * 1000);
    reconnectTimer.current = setTimeout(connectFn, delay);
  }, []);

  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    let token = null;
    try { token = localStorage.getItem("vella_token"); } catch {}
    if (!token) return; // Don't connect without auth

    try {
      const ws = new WebSocket(`${wsUrl}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "heartbeat") return;

          setTasks((prev) => {
            const existing = prev.findIndex((t) => t.id === data.taskId || t.id === data.id);
            const taskUpdate = {
              id: data.taskId || data.id || Date.now().toString(),
              type: data.taskType || data.type || "task",
              status: data.status || data.event?.replace("task:", "") || "queued",
              message: data.message || data.result?.substring?.(0, 100) || "",
              progress: data.progress || 0,
              createdAt: data.createdAt || new Date().toISOString(),
            };

            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = { ...updated[existing], ...taskUpdate };
              return updated;
            }
            return [taskUpdate, ...prev].slice(0, 50);
          });

          if (!isOpen) {
            setUnreadCount((c) => c + 1);
          }
        } catch (e) {
          console.warn("[TaskProgress] Parse error:", e);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        scheduleReconnect(connectWs);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      console.warn("[TaskProgress] Connection error:", e);
      scheduleReconnect(connectWs);
    }
  }, [wsUrl, isOpen, scheduleReconnect]);

  useEffect(() => {
    connectWs();
    return () => {
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connectWs]);

  useEffect(() => {
    if (isOpen) setUnreadCount(0);
  }, [isOpen]);

  const activeTasks = tasks.filter((t) => t.status !== "completed" && t.status !== "failed");
  const recentTasks = tasks.slice(0, 20);

  // Hide entirely when not connected and no tasks to show
  const hasActivity = tasks.length > 0;
  if (!isConnected && !hasActivity) return null;

  return (
    <>
      {/* Floating Toggle Button - only show when connected or has tasks */}
      {(isConnected || hasActivity) && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            position: "fixed",
            bottom: 140,
            right: 18,
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.72)",
            backdropFilter: "blur(20px) saturate(180%)",
            WebkitBackdropFilter: "blur(20px) saturate(180%)",
            color: "#0A1B3A",
            border: "1px solid rgba(255,255,255,0.55)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 16px -4px rgba(11,107,211,0.25), 0 1px 0 rgba(255,255,255,0.85) inset",
            zIndex: 600,
            transition: "transform 200ms ease, background 200ms ease",
            opacity: activeTasks.length > 0 ? 1 : 0.85,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.06)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
          title={isConnected ? `Task Progress (${activeTasks.length} active)` : "Task Progress"}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={isConnected ? BLUE : GRAY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
          {unreadCount > 0 && (
            <span style={{
              position: "absolute",
              top: -3,
              right: -3,
              background: RED,
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              borderRadius: "50%",
              width: 16,
              height: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1.5px solid #FFFFFF",
              boxShadow: "0 1px 3px rgba(220,38,38,0.4)",
              lineHeight: 1,
            }}>
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Floating Panel */}
      {isOpen && (
        <div style={{
          position: "fixed",
          bottom: 188,
          right: 18,
          width: 360,
          maxHeight: 480,
          background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)",
          backdropFilter: "blur(34px) saturate(180%)",
          WebkitBackdropFilter: "blur(34px) saturate(180%)",
          borderRadius: 18,
          boxShadow: "0 1px 0 rgba(255,255,255,0.70) inset, 0 18px 44px -18px rgba(11,107,211,0.28), 0 8px 24px -10px rgba(14,165,233,0.18)",
          zIndex: 9998,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.40)",
        }}>
          {/* Header */}
          <div style={{
            padding: "16px 20px",
            borderBottom: "1px solid rgba(255,255,255,0.45)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "rgba(255,255,255,0.35)",
            backdropFilter: "blur(18px) saturate(180%)",
            WebkitBackdropFilter: "blur(18px) saturate(180%)",
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1F2937" }}>
                Task Progress
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                <span style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: isConnected ? GREEN : YELLOW,
                  display: "inline-block",
                }} />
                <span style={{ fontSize: 11, color: GRAY }}>
                  {isConnected ? "Connected" : "Connecting..."}
                  {activeTasks.length > 0 && ` . ${activeTasks.length} active`}
                </span>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
                color: GRAY,
                padding: 4,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          {/* Task List */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {recentTasks.length === 0 ? (
              <div style={{
                padding: 40,
                textAlign: "center",
                color: GRAY,
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={GRAY} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                <p style={{ fontSize: 13, margin: 0 }}>No tasks yet</p>
                <p style={{ fontSize: 12, margin: "4px 0 0", color: "#9CA3AF" }}>
                  Tasks will appear here in real-time
                </p>
              </div>
            ) : (
              recentTasks.map((task) => <TaskItem key={task.id} task={task} />)
            )}
          </div>
        </div>
      )}
    </>
  );
}
